import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Box, useTheme, Tooltip, Button } from '@mui/material';
import { Add, Gavel } from '@mui/icons-material';

import { usePagination, Column } from '@frontend/features/common/components/Table';
import { ListLayout } from '@frontend/features/common/components/ListLayout';
import { ListContentLayout } from '@frontend/features/common/components/ListContentLayout';
import { useSessionFilters } from '@frontend/features/cases/hooks/useSessionFilters';
import { useGetHotelVendorHotels } from '@frontend/features/hotel-content-manager/queries';
import { VendorHotel } from '@bandido/shared/interfaces';
import { OrderOptionsValues } from '@bandido/shared/enums';
import { VendorHotelsExtractorModal } from '@frontend/features/hotel-content-manager/components/VendorHotelsExtractorModal/vendorHotelsExtractorModal';
import { client } from '@frontend/features/common/services';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { VendorHotelLinkModal } from '@frontend/features/hotel-content-manager/components/VendorHotelLinkModal';

export const VendorHotelsView = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const { addToast } = useSnackBar();
  const { t } = useTranslation();
  const pagination = usePagination();

  const [selectedItems, setSelectedItems] = useState<(string | number)[]>([]);
  const [isExtractionModalOpen, setIsExtractionModalOpen] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [selectedVendorHotel, setSelectedVendorHotel] = useState<VendorHotel | null>(null);
  const { data: vendorHotels, isLoading: isLoadingVendorHotels } = useGetHotelVendorHotels({
    searchText: '',
    supplierIds: [],
    categoryIds: [],
    countries: [],
    currentPage: pagination.page,
    pageSize: pagination.rowsPerPage,
    sort: 'id',
    order: OrderOptionsValues.asc,
  });

  const { updateFilters, clearFilters } = useSessionFilters(searchParams, setSearchParams);

  const handleSelectedItemsChange = (newSelectedItems: (string | number)[]) => {
    setSelectedItems(newSelectedItems);
  };

  const handleModerateSelected = () => {
    client.post('/content-extractor/moderate-hotels', {
      vendorHotelIds: selectedItems,
    }).then(() => {
      addToast({
        message: t('hotelContent.management.vendorHotels.moderate.success'),
        color: 'success',
      });
      setSelectedItems([]);
    }).catch((_err) => {
      addToast({
        message: t('hotelContent.management.vendorHotels.moderate.error'),
        color: 'error',
      });
    });
  };

  const handleOpenLinkModal = (hotel: any) => {
    setSelectedVendorHotel(hotel);
    setIsLinkModalOpen(true);
  };

  const handleCloseLinkModal = () => {
    setIsLinkModalOpen(false);
    setSelectedVendorHotel(null);
  };

  const handleClearFilters = () => {
    clearFilters();
  };

  // Define columns for the vendor hotels table
  const columns: Column<any>[] = [
    {
      id: 'name',
      label: t('hotelContent.management.vendorHotels.table.name') || 'Hotel Name',
      align: 'left' as const,
      canSort: true,
      sortField: 'name',
      minWidth: 250,
      format: (_: any, item: any) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ fontWeight: 'medium', fontSize: '0.875rem' }}>{item.name}</Box>
          <Box sx={{ fontSize: '0.75rem', color: theme.palette.text.secondary }}>{item.code}</Box>
        </Box>
      ),
    },
    {
      id: 'location',
      label: t('hotelContent.management.vendorHotels.table.location') || 'Location',
      align: 'left' as const,
      canSort: true,
      sortField: 'city',
      minWidth: 200,
      format: (_: any, item: any) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ fontSize: '0.875rem' }}>
            {item.city}, {item.state}
          </Box>
          <Box sx={{ fontSize: '0.75rem', color: theme.palette.text.secondary }}>{item.country}</Box>
        </Box>
      ),
    },
    {
      id: 'address',
      label: t('hotelContent.management.vendorHotels.table.address') || 'Address',
      align: 'left' as const,
      canSort: true,
      minWidth: 200,
      format: (_: any, item: any) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ fontSize: '0.875rem' }}>{item.address}</Box>
          <Box sx={{ fontSize: '0.75rem', color: theme.palette.text.secondary }}>{item.zip_code}</Box>
        </Box>
      ),
    },
    {
      id: 'supplier_id',
      label: t('hotelContent.management.vendorHotels.table.supplier') || 'Supplier',
      align: 'left' as const,
      canSort: true,
      minWidth: 120,
    },
    {
      id: 'parent_id',
      label: t('hotelContent.management.vendorHotels.table.parent') || 'Parent Hotel',
      align: 'left' as const,
      canSort: true,
      minWidth: 120,
      format: (_: any, item: any) => <Box sx={{ fontSize: '0.875rem' }}>{item.parent_id || '-'}</Box>,
    },
    {
      id: 'actions',
      label: '',
      align: 'right' as const,
      canSort: false,
      format: (_: any, item: any) => (
        <Button variant="contained" size="small" color="primary" onClick={() => handleOpenLinkModal(item)}>
          Link
        </Button>
      ),
    },
  ];

  // Transform data for table
  const tableData = useMemo(() => {
    if (vendorHotels) {
      return {
        key: 'id',
        items: vendorHotels.items.map((hotel) => ({
          id: hotel.id,
          name: hotel.name,
          code: hotel.code,
          city: hotel.city,
          address: hotel.address,
          zip_code: hotel.zipCode,
          state: hotel.state,
          country: hotel.country,
          parent_id: hotel.nimbuxParentId,
          supplier_id: hotel.supplierId,
        })),
        totalCount: vendorHotels.count,
      };
    }
    return undefined;
  }, [vendorHotels]);

  const filtersCount = useMemo(() => {
    const keysToOmit = ['sort', 'order', 'page', 'search'];
    let count = 0;

    searchParams.forEach((value, key) => {
      if (!keysToOmit.includes(key) && value) {
        count++;
      }
    });

    return count;
  }, [searchParams]);

  const createExtractionButton = (
    <Tooltip title={t('hotelContent.management.vendorHotels.createExtraction') || 'Create Extraction'} arrow>
      <Button
        variant="contained"
        onClick={() => setIsExtractionModalOpen(true)}
        disabled={isLoadingVendorHotels}
        aria-label="Create Extraction"
        sx={{
          height: theme.spacing(5),
          minWidth: theme.spacing(5),
          padding: theme.spacing(1),
        }}
      >
        <Add />
      </Button>
    </Tooltip>
  );

  const checkboxActions = (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Button variant="text" startIcon={<Gavel />} onClick={handleModerateSelected} size="small" color="primary">
        {t('hotelContent.management.vendorHotels.moderate.title') || 'Moderate'}
      </Button>
    </Box>
  );

  const filtersOptions = {
    form: (
      <Box sx={{ p: 2, minWidth: 300 }}>
        {/* Add filter form components here when needed */}
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Button onClick={handleClearFilters} variant="outlined">
            {t('common.clearFilters') || 'Clear Filters'}
          </Button>
        </Box>
      </Box>
    ),
    filterListKey: 'vendorhotelslist',
    searchParams,
    setSearchParams,
    onClearFilters: handleClearFilters,
  };

  return (
    <>
      <ListLayout
        title={t('hotelContent.management.vendorHotels.title') || 'Vendor Hotels'}
        subtitle={t('hotelContent.management.vendorHotels.subtitle') || 'Manage and view vendor hotel listings'}
      >
        <ListContentLayout
          pagination={pagination}
          filtersCount={filtersCount}
          stickyHeader
          filters={filtersOptions}
          onSearchChange={(value: string) => {
            updateFilters({ search: value });
          }}
          statisticsButton={createExtractionButton}
          columns={columns}
          isLoading={isLoadingVendorHotels}
          data={tableData}
          enableCheckboxSelection
          selectedItems={selectedItems}
          onSelectedItemsChange={handleSelectedItemsChange}
          checkboxActions={checkboxActions}
        />
      </ListLayout>
      <VendorHotelsExtractorModal open={isExtractionModalOpen} onClose={() => setIsExtractionModalOpen(false)} />
      {selectedVendorHotel && (
        <VendorHotelLinkModal
          key={`${selectedVendorHotel.id}_${selectedVendorHotel.nimbuxParentId}`}
          open={isLinkModalOpen}
          onClose={handleCloseLinkModal}
          vendorHotelId={selectedVendorHotel.id!}
        />
      )}
    </>
  );
};
